/**
 * @fileoverview Error boundary for EditStudentsModal.
 * Catches React render errors and shows a fallback UI instead of crashing the app.
 * This guarantees no white screen — errors are contained inside the modal.
 */
import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { EditStudentsErrorFallback } from './EditStudentsErrorFallback'

interface Props {
  children: ReactNode
  onClose: () => void
}

interface State {
  hasError: boolean
}

/**
 * Error boundary that wraps EditStudentsModal content.
 * If any child crashes, shows a friendly error message with a close button.
 */
export class EditStudentsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[EditStudents] Component error:', error, errorInfo)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <EditStudentsErrorFallback onClose={this.props.onClose} />
    }
    return this.props.children
  }
}
